import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

interface GalaxyBackgroundProps {
  variant?: "default" | "orangeWhite" | "cosmic" | "dark";
  transparent?: boolean;
  enableZoomAnimation?: boolean;
}

const GalaxyBackground = ({ 
  variant = "orangeWhite", 
  transparent = false,
  enableZoomAnimation = false 
}: GalaxyBackgroundProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInViewport, setIsInViewport] = useState(false);
  const initializedRef = useRef(false);

  // Defer initialization until component is in viewport
  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !initializedRef.current) {
            setIsInViewport(true);
            initializedRef.current = true;
            observer.disconnect();
          }
        });
      },
      { threshold: 0.01, rootMargin: "50px" } // Start loading slightly before visible
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!containerRef.current || !isInViewport) return;

    // Scene setup
    const scene = new THREE.Scene();
    if (transparent) {
      scene.background = null;
    } else {
      scene.background = new THREE.Color(0x160016);
    }
    
    // Safe window access
    if (typeof window === 'undefined') return;
    
    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      1,
      1000
    );
    // Set initial camera position based on whether animation is enabled
    if (enableZoomAnimation) {
      camera.position.set(0, 4, 12); // Start zoomed in for dramatic reveal
    } else {
      camera.position.set(0, 4, 21); // Default position
    }

    // Optimized renderer settings for performance
    const renderer = new THREE.WebGLRenderer({ 
      antialias: false, // Disabled for better performance
      alpha: transparent,
      powerPreference: "high-performance",
      stencil: false,
      depth: true
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Cap at 2x for performance
    if (transparent) {
      renderer.setClearColor(0x000000, 0);
    }
    
    // Style the renderer
    // 'manipulation' allows scrolling and pinch zoom but prevents double-tap zoom
    // This lets OrbitControls handle single-touch rotation while allowing page scroll
    renderer.domElement.style.touchAction = 'manipulation';
    renderer.domElement.style.userSelect = 'none'; // Prevent text selection
    
    containerRef.current.appendChild(renderer.domElement);

    // OrbitControls - Full interaction enabled for galaxy manipulation
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enablePan = false;
    controls.enableZoom = false; // Disable mouse wheel zoom to allow page scrolling
    controls.enableRotate = true; // Allow rotation with mouse/touch drag
    controls.rotateSpeed = 0.5; // Smooth rotation
    controls.minDistance = 5; // Minimum zoom distance
    controls.maxDistance = 100; // Maximum zoom distance
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.ROTATE
    };
    // Touch gesture support (mobile)
    // ONE finger = rotate galaxy
    // TWO fingers = undefined (allows page scroll to work)
    controls.touches = {
      ONE: THREE.TOUCH.ROTATE,
      TWO: undefined as any // Disable two-finger gestures to allow page scrolling
    };
    
    // Smart wheel handling: Allow Ctrl+scroll for galaxy zoom, normal scroll for page
    const handleWheel = (e: WheelEvent) => {
      // Only capture Ctrl+scroll (pinch-to-zoom gesture on some trackpads)
      // Regular two-finger scroll (without Ctrl) will pass through to scroll the page
      if (e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();
        
        // Manual zoom implementation for Ctrl+scroll
        const zoomDelta = e.deltaY * -0.01;
        const distance = camera.position.length();
        const newDistance = Math.max(5, Math.min(100, distance * (1 + zoomDelta)));
        
        camera.position.normalize().multiplyScalar(newDistance);
        camera.lookAt(0, 0, 0);
      }
      // If not Ctrl+scroll, let it pass through (page will scroll)
    };
    renderer.domElement.addEventListener('wheel', handleWheel, { passive: false });

    // Resize handler
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", handleResize);

    // Galaxy particles
    const gu = { time: { value: 0 } };
    const sizes: number[] = [];
    const shift: number[] = [];
    
    const pushShift = () => {
      shift.push(
        Math.random() * Math.PI,
        Math.random() * Math.PI * 2,
        (Math.random() * 0.9 + 0.1) * Math.PI * 0.1,
        Math.random() * 0.9 + 0.1
      );
    };

    const pts: THREE.Vector3[] = [];
    
    // Performance optimization: Significantly reduced particle count
    // Reduced from 127,500 to ~30,000 particles for better performance
    const INNER_PARTICLES = 10000; // Reduced from 42,500 (76% reduction)
    const DISK_PARTICLES = 20000; // Reduced from 85,000 (76% reduction)

    // Inner sphere particles - Optimized count for performance
    for (let i = 0; i < INNER_PARTICLES; i++) {
      sizes.push(Math.random() * 1.5 + 0.5);
      pushShift();
      pts.push(
        new THREE.Vector3()
          .randomDirection()
          .multiplyScalar(Math.random() * 0.5 + 9.5)
      );
    }

    // Disk particles - Optimized count for performance
    for (let i = 0; i < DISK_PARTICLES; i++) {
      const r = 10, R = 40;
      const rand = Math.pow(Math.random(), 1.5);
      const radius = Math.sqrt(R * R * rand + (1 - rand) * r * r);
      pts.push(
        new THREE.Vector3().setFromCylindricalCoords(
          radius,
          Math.random() * 2 * Math.PI,
          (Math.random() - 0.5) * 2
        )
      );
      sizes.push(Math.random() * 1.5 + 0.5);
      pushShift();
    }

    const geometry = new THREE.BufferGeometry().setFromPoints(pts);
    geometry.setAttribute("sizes", new THREE.Float32BufferAttribute(sizes, 1));
    geometry.setAttribute("shift", new THREE.Float32BufferAttribute(shift, 4));

    const material = new THREE.PointsMaterial({
      size: 0.125,
      transparent: true,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });

    // Color gradient based on variant
    const colorShader = variant === "orangeWhite" 
      ? `// Deep Orange gradient palette - Extra saturated
          vec3 brightOrange = vec3(255., 120., 40.) / 255.;     // More red, less yellow
          vec3 deepOrange = vec3(230., 90., 20.) / 255.;        // Very deep orange
          vec3 darkOrange = vec3(200., 70., 10.) / 255.;        // Dark burnt orange
          vec3 coreOrange = vec3(255., 140., 50.) / 255.;       // Bright core orange
          
          // Create more saturated orange gradient
          vec3 coreColor = mix(coreOrange, brightOrange, d * 0.3);
          vec3 outerColor = mix(deepOrange, darkOrange, d * 0.8);
          
          vColor = mix(coreColor, outerColor, smoothstep(0.2, 0.9, d));`
      : `vColor = mix(vec3(227., 155., 0.), vec3(100., 50., 255.), d) / 255.;`;

    // Shader customization
    material.onBeforeCompile = (shader) => {
      shader.uniforms.time = gu.time;
      shader.vertexShader = `
        uniform float time;
        attribute float sizes;
        attribute vec4 shift;
        varying vec3 vColor;
        ${shader.vertexShader}
      `
        .replace(`gl_PointSize = size;`, `gl_PointSize = size * sizes;`)
        .replace(
          `#include <color_vertex>`,
          `#include <color_vertex>
          float d = length(abs(position) / vec3(40., 10., 40));
          d = clamp(d, 0., 1.);
          ${colorShader}
        `
        )
        .replace(
          `#include <begin_vertex>`,
          `#include <begin_vertex>
          float t = time;
          float moveT = mod(shift.x + shift.z * t, PI2);
          float moveS = mod(shift.y + shift.z * t, PI2);
          transformed += vec3(cos(moveS) * sin(moveT), cos(moveT), sin(moveS) * sin(moveT)) * shift.w;
        `
        );

      shader.fragmentShader = `
        varying vec3 vColor;
        ${shader.fragmentShader}
      `
        .replace(
          `vec4 diffuseColor = vec4( diffuse, opacity );`,
          `float d = length(gl_PointCoord.xy - 0.5);
        vec4 diffuseColor = vec4( vColor, smoothstep(0.5, 0.1, d) );`
        );
    };

    const points = new THREE.Points(geometry, material);
    points.rotation.order = "ZYX";
    points.rotation.z = 0.2;
    scene.add(points);

    // Animation loop
    const clock = new THREE.Clock();
    
    // Cinematic zoom-out animation (optional)
    const animationDuration = 4000; // 4 seconds
    const startDistance = 12;
    const endDistance = 36;
    const startY = 4;
    const endY = 4;
    const animationStartTime = Date.now();
    let animationComplete = !enableZoomAnimation; // Skip if disabled
    
    const easeOutCubic = (t: number) => {
      return 1 - Math.pow(1 - t, 3);
    };
    
    // Performance optimization: Throttle animation loop and reduce update frequency when tab is inactive
    let frameCount = 0;
    let lastFrameTime = performance.now();
    const targetFPS = 30; // Reduce from 60fps to 30fps for better performance
    const frameInterval = 1000 / targetFPS;

    const animate = (currentTime: number) => {
      // Throttle animation to target FPS
      const elapsed = currentTime - lastFrameTime;
      if (elapsed < frameInterval) {
        requestAnimationFrame(animate);
        return;
      }
      
      lastFrameTime = currentTime - (elapsed % frameInterval);

      // Skip updates if tab is not visible (performance optimization)
      if (document.hidden) {
        requestAnimationFrame(animate);
        return;
      }

      controls.update();
      const t = clock.getElapsedTime() * 0.5;
      gu.time.value = t * Math.PI;
      points.rotation.y = t * 0.05;
      
      // Cinematic zoom-out animation
      if (!animationComplete && enableZoomAnimation) {
        const elapsed = Date.now() - animationStartTime;
        const progress = Math.min(elapsed / animationDuration, 1);
        const easedProgress = easeOutCubic(progress);
        
        // Animate camera distance
        const currentDistance = startDistance + (endDistance - startDistance) * easedProgress;
        const currentY = startY + (endY - startY) * easedProgress;
        
        // Set camera position
        camera.position.z = currentDistance;
        camera.position.x = 0;
        camera.position.y = currentY;
        
        camera.lookAt(0, 0, 0);
        
        // Mark animation as complete
        if (progress >= 1) {
          animationComplete = true;
        }
      }
      
      renderer.render(scene, camera);
      frameCount++;
      requestAnimationFrame(animate);
    };
    
    // Start animation loop
    requestAnimationFrame(animate);
    
    // Visibility change handler - pause when tab is hidden
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden, renderer will skip updates in animate loop
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      renderer.domElement.removeEventListener("wheel", handleWheel);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Animation loop cleanup is handled by requestAnimationFrame chain
      controls.dispose();
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (containerRef.current?.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
    };
  }, [variant, transparent, enableZoomAnimation, isInViewport]);

  return (
    <div
      ref={containerRef}
      className="absolute top-0 left-0 w-full h-full"
      style={{ 
        zIndex: 0,
        pointerEvents: 'auto', // Allow interaction but wheel events will pass through
        backgroundColor: isInViewport ? 'transparent' : '#000000' // Show black background while loading
      }}
    />
  );
};

export default GalaxyBackground;

